import { ApiError, api } from './api';

/**
 * Offline mutation queue (ADR-005).
 *
 * A craving at 3am with no signal must still be recorded. Mutations are stored
 * in IndexedDB and replayed when the connection returns; every payload carries
 * a clientId, so replaying can never duplicate an event.
 */

const DB_NAME = 'respiria';
const DB_VERSION = 1;
const STORE = 'pending-mutations';

export interface PendingMutation {
  id: string;
  path: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body: unknown;
  createdAt: number;
  attempts: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = fn(tx.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}

/** Fired whenever the queue changes, so the UI can show what's pending to sync. */
export const QUEUE_EVENT = 'respiria:queue-change';
function emitQueueChange(): void {
  window.dispatchEvent(new Event(QUEUE_EVENT));
}

export async function enqueue(mutation: Omit<PendingMutation, 'createdAt' | 'attempts'>): Promise<void> {
  await withStore('readwrite', (store) =>
    store.put({ ...mutation, createdAt: Date.now(), attempts: 0 }),
  );
  emitQueueChange();
}

export async function pending(): Promise<PendingMutation[]> {
  const all = await withStore<PendingMutation[]>('readonly', (store) => store.getAll());
  return all.sort((a, b) => a.createdAt - b.createdAt);
}

/** How many mutations are waiting to sync. */
export async function pendingCount(): Promise<number> {
  return (await pending()).length;
}

async function remove(id: string): Promise<void> {
  await withStore('readwrite', (store) => store.delete(id));
  emitQueueChange();
}

async function bumpAttempts(mutation: PendingMutation): Promise<void> {
  await withStore('readwrite', (store) => store.put({ ...mutation, attempts: mutation.attempts + 1 }));
}

/**
 * Replays everything pending, oldest first. Stops at the first network failure
 * so ordering is preserved; drops mutations the server rejects outright, since
 * retrying a 400 forever would block the queue.
 */
export async function flush(): Promise<{ sent: number; failed: number }> {
  if (!navigator.onLine) return { sent: 0, failed: 0 };

  let sent = 0;
  let failed = 0;

  for (const mutation of await pending()) {
    try {
      await api(mutation.path, {
        method: mutation.method,
        body: mutation.body === undefined ? undefined : JSON.stringify(mutation.body),
      });
      await remove(mutation.id);
      sent++;
    } catch (err) {
      if (err instanceof ApiError) {
        // Session gone (e.g. expired while offline): never drop the user's data.
        // Keep the whole queue and stop; it replays after they sign back in.
        if (err.status === 401 || err.status === 403) break;
        if (err.status >= 400 && err.status < 500) {
          // Malformed or already-applied: keeping it would block everything behind it.
          await remove(mutation.id);
          failed++;
          continue;
        }
      }
      if (mutation.attempts >= 10) {
        await remove(mutation.id);
        failed++;
        continue;
      }
      await bumpAttempts(mutation);
      break; // network is down — try again on the next online event
    }
  }

  return { sent, failed };
}

/**
 * Sends a mutation, falling back to the queue when offline. Returns null when
 * it was queued, so callers can show an optimistic state.
 */
export async function sendOrQueue<T>(
  id: string,
  path: string,
  method: PendingMutation['method'],
  body: unknown,
): Promise<T | null> {
  try {
    return await api<T>(path, {
      method,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    const isNetworkError = !(err instanceof ApiError);
    if (isNetworkError || !navigator.onLine) {
      await enqueue({ id, path, method, body });
      return null;
    }
    throw err;
  }
}

/** Flushes on reconnect and once at startup. */
export function startOfflineSync(onFlush?: () => void): () => void {
  const run = () => {
    void flush().then((result) => {
      if (result.sent > 0) onFlush?.();
    });
  };

  window.addEventListener('online', run);
  run();
  return () => window.removeEventListener('online', run);
}
